/*
  licenseGate.js — License key validation

  Validates license keys against the Lemon Squeezy API.
  A valid key unlocks paid features for the current session.

  Keys are stored in localStorage so the license persists across
  sessions — Pro users don't need to re-enter their key every visit.
  They are NOT tied to an account — the key is just cached locally.

  Key format: AISS-XXXX-XXXX-XXXX (generated by Lemon Squeezy)
*/

// LocalStorage key — persists across sessions so Pro users
// don't have to re-enter their key every time they open the app
const SESSION_KEY = "aissift_license_valid";

// Safe localStorage helpers — some browsers block localStorage in
// private/incognito mode and throw a SecurityError. These never throw.
function storageGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function storageSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* silently fail */ }
}
function storageRemove(key) {
  try { localStorage.removeItem(key); } catch { /* silently fail */ }
}

/*
  isUnlocked — checks if this browser has a validated license.
*/
export function isUnlocked() {
  return storageGet(SESSION_KEY) === "true";
}

/*
  validateKey — validates a license key against Lemon Squeezy.

  key: the license key string entered by the user
  Returns a Promise resolving to { valid: bool, message: string }

  NOTE: During development, any key starting with "DEV-" will pass
  so you can test paid features without a real Lemon Squeezy account.
  Remove this before going live.
*/
export async function validateKey(key) {
  if (!key || key.trim().length === 0) {
    return { valid: false, message: "Please enter a license key." };
  }

  const trimmed = key.trim().toUpperCase();

  // Development bypass — remove before launch
  if (trimmed.startsWith("DEV-")) {
    storageSet(SESSION_KEY, "true");
    return { valid: true, message: "Development key accepted." };
  }

  // Real Lemon Squeezy validation
  try {
    const response = await fetch("https://api.lemonsqueezy.com/v1/licenses/validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        license_key: trimmed,
        instance_name: "AISsift Web",
      }),
    });

    if (!response.ok) {
      return {
        valid: false,
        message: "Could not validate key — check your connection and try again.",
      };
    }

    const data = await response.json();

    if (data.valid) {
      storageSet(SESSION_KEY, "true");
      return { valid: true, message: "License key accepted. Paid features unlocked." };
    } else {
      return { valid: false, message: "Invalid license key. Please check and try again." };
    }
  } catch (error) {
    return {
      valid: false,
      message: "Could not validate key — check your connection and try again.",
    };
  }
}

/*
  clearLicense — removes the cached license from the session.
  Useful for testing or if a user wants to switch keys.
*/
export function clearLicense() {
  storageRemove(SESSION_KEY);
}
